import { Component, computed, effect, inject, input, linkedSignal, OnInit, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Client, UpdateClient } from '../../../api/app-types/client.app';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { County } from '../../../api/app-types/county.app';
import { Gender } from '../../../api/app-types/gender.app';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import { validateBirthDateAndSetWarning } from '../../../shared/helpers/validate-birthdate.func';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { ClientDeleteDialog } from './client-delete-dialog';
import { mapDateOrNull, matDateProviders } from '../../../shared/helpers/date.func';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';

@Component({
	selector: 'lvr-client-details-edit',
	imports: [
		MatFormFieldModule,
		MatCheckbox,
		MatSelectModule,
		MatTooltipModule,
		TranslocoModule,
		MatButtonModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatRadioModule,
		MatInputModule,
		MatNativeDateModule,
		MatIconModule,
		LvrInputDirective,
		LvrAutoSaveDirective,
		LvrAppendAsteriskPipe,
		AddressCompletion,
		LvrContactPoint,
		LvrPaddingDirective,
		LvrRestoreActions,
		LvrField,
		LvrDefaultActions,
		LvrButton,
		LvrSearchableSelect,
	],
	templateUrl: './client-details-edit.html',
	styleUrl: './client-details-edit.scss',
	providers: [matDateProviders],
})
export class ClientDetailsEdit extends LvrRedirectNav implements OnInit {
	private readonly recordManager = inject(RECORD_MANAGER);

	public readonly client = input<Client | undefined>();
	public readonly isSaving = input.required<boolean>();
	public readonly existingContactPoints = computed(() => this.client()?.contactPoints || []);
	private readonly dialog = inject(MatDialog);
	public save = output<UpdateClient>();
	public cancelEdit = output<void>();
	public maxDate = new Date();
	public minDate = new Date();
	public readonly draftId = computed(() => {
		return `${FORM_KEYS.UPDATE_CLIENT}.${this.client()?.id}`;
	});
	public minDateFormatted = '';
	public readonly compareById = compareById;
	public readonly showBirthdayWarning = signal<boolean>(false);
	public readonly showAnonymizeClientPopup = signal<boolean>(false);
	public readonly anonymizeClient = signal<boolean>(false);
	private readonly environment = inject(ENVIRONMENT);
	private readonly nameRegex = new RegExp(this.environment.nameRegexStr);
	private readonly titleRegex = new RegExp(this.environment.titleRegexStr);
	public readonly birthDateWarningRange = {
		min: this.environment.formConfiguration.client.birthdateWarningRange.min,
		max: this.environment.formConfiguration.client.birthdateWarningRange.max,
	};
	public readonly dateFormatHint = this.environment.visualDateFormat;
	public readonly birthdateLimitYears = this.environment.birthdateLimitYears; // 100 years

	private readonly fb = inject(FormBuilder);
	public readonly form = this.fb.group({
		name: this.fb.control(this.client()?.name, [Validators.required, Validators.pattern(this.nameRegex)]),
		gender: this.fb.control(this.client()?.gender),
		firstName: this.fb.control(this.client()?.firstName, Validators.pattern(this.nameRegex)),
		birthName: this.fb.control(this.client()?.birthName, Validators.pattern(this.nameRegex)),
		dateOfBirth: this.fb.control<Date | undefined>(undefined),
		title: this.fb.control(this.client()?.title, Validators.pattern(this.titleRegex)),
		address: this.fb.control(this.client()?.address),
		residenceDistrict: this.fb.control(this.client()?.residenceDistrict),
		communicationWays: this.fb.control<ContactPoint[]>(this.existingContactPoints()),
		numberKV: this.fb.control(this.client()?.numberKV),
		numberSV: this.fb.control(this.client()?.numberSV),
		numberBA: this.fb.control(this.client()?.numberBA),
	});

	private readonly dateOfBirthCtrl = this.form.get('dateOfBirth');

	private readonly dateOfBirthValue = toSignal(this.dateOfBirthCtrl!.valueChanges, {
		initialValue: this.dateOfBirthCtrl!.value,
	});
	public readonly communicationWayCount = linkedSignal(() => this.form.get('communicationWays')?.value?.length || 0);
	public readonly communicationWayCountArrayKeys = computed(() => Array(this.communicationWayCount()));
	public readonly matcher = new ImmediateErrorStateMatcher();

	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly valueChanged = toSignal(this.form?.valueChanges);
	public readonly canDeleteClient = hasPermission(this.session.permissions, [
		'Clients.Delete',
		'Clients.Delete.PhaseOneOnly',
	]);
	public readonly canEditClientPermission = hasPermission(this.session.permissions, [
		'Clients.Edit',
		'Clients.Edit.PhaseOneOnly',
	]);
	public readonly availableGenders = this.resolveDataAsSignal<Gender[]>('genders');
	public readonly countyPage = this.resolveDataAsSignal<PaginatedResponse<County>>('counties');
	public readonly availableResidenceDistrict = computed(() => this.countyPage()?.entities || []);
	private readonly contactPointCategoryPage =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly contactPointCategories = computed(
		() => (this.contactPointCategoryPage()?.entities || []) as ContactPointCategory[],
	);

	public get communicationWays() {
		return (this.form.get('communicationWays')?.value ?? []) as ContactPoint[];
	}

	public constructor() {
		super();
		effect(() => {
			const client = this.client();
			if (!client) return;

			this.form.patchValue({
				...client,
				dateOfBirth: mapDateOrNull(client.dateOfBirth) ?? undefined,
				gender: client.gender,
				communicationWays: client.contactPoints,
			});
		});

		effect(() => {
			const value = this.dateOfBirthValue();

			if (typeof value !== 'string') return;

			this.dateOfBirthCtrl?.setValue(mapDateOrNull(value) ?? undefined, {
				emitEvent: false,
			});
		});

		effect(() => {
			const value = this.valueChanged()?.dateOfBirth;
			validateBirthDateAndSetWarning(value, this.showBirthdayWarning, this.birthDateWarningRange);
		});
	}

	public async ngOnInit() {
		// Set min date for Datepicker
		this.setMinDates();
	}

	/**
	 * Checks if a form control has a specific validation error and has been touched.
	 * Used for LVR-Error.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if any error exists and the control was touched.
	 */
	public hasError = (control: AbstractControl | null, ...errors: string[]) => {
		if (!control) return false;

		return errors.some(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);
	};

	public get addressValue() {
		return this.form.get('address')?.value;
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	/**
	 * Emits the form value if the update form is valid.
	 * Used to trigger a save action from the parent component.
	 */
	public async onSave() {
		if (this.anonymizeClient()) {
			const confirmed = await this.triggerAnonymizeClient();

			if (!confirmed) return;

			this.draftService.deleteDraft('client', this.draftId());
			this.recordManager.closeAndSwitchToNextRecord();

			return;
		}

		if (this.form.valid) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const formValue = this.form.value as any;

			Object.keys(formValue).forEach((k) => {
				if (typeof formValue[k] === 'string') formValue[k] = formValue[k].trim();
			});

			this.save.emit(formValue);
			this.draftService.deleteDraft('client', this.draftId());
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public checkboxChanged(event: any) {
		this.anonymizeClient.set(event.checked);
	}

	public async triggerAnonymizeClient() {
		const dialogRef = this.dialog.open(ClientDeleteDialog, {
			data: this.client(),
			width: '500px',
			disableClose: true,
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		return !!result;
	}

	/**
	 * Cancels the edit mode and discards any unsaved changes.
	 * Deletes the saved draft and emits a cancel event to the parent.
	 */
	public handleCancelEdit() {
		const currentClient = this.client();
		if (!currentClient) {
			return;
		}

		this.draftService.deleteDraft('client', this.draftId());
		this.cancelEdit.emit();
	}

	/**
	 * Sets min dates for the Datepicker.
	 */
	private setMinDates() {
		this.minDate.setFullYear(this.maxDate.getFullYear() - this.birthdateLimitYears);
		this.minDateFormatted = this.minDate.toLocaleDateString('de-DE', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	}

	public addCommunicationWay() {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		this.form.get('communicationWays')?.patchValue(values);
	}

	public removeCommunicationWay(index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			this.form.get('communicationWays')?.patchValue(values);
		}
	}
}
